import React from 'react';
import { useLicense } from '../contexts/LicenseContext';

interface WorkflowChoiceProps {
  onSelectWorkflow: (workflow: 'new' | 'continue') => void;
  onLoadSessions: () => void;
}

const WorkflowChoice: React.FC<WorkflowChoiceProps> = ({
  onSelectWorkflow,
  onLoadSessions
}) => {
  const { licenseInfo } = useLicense();

  return (
    <div className="max-w-4xl mx-auto mb-8">
      {/* Post-login welcome acknowledgment — makes this the first thing a
          freshly-signed-in user sees, tying the tier/trial they just
          activated to their next action, rather than a generic chooser. */}
      {licenseInfo && licenseInfo.tier !== 'NO_LICENSE' && (
        <p className="text-center text-sm text-neutral-500 mb-4">
          You're signed in on the <span className="font-semibold text-neutral-700">{licenseInfo.tier}</span> plan.
        </p>
      )}

      <div className="bg-white rounded-2xl shadow-soft-lg p-8 border border-neutral-200 hover:shadow-soft-xl transition-shadow duration-300">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-display font-bold text-neutral-900 mb-2">
            How would you like to start?
          </h2>
          <p className="text-lg text-neutral-600">
            Create a new project or continue working on an existing session
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* New Project Option */}
          <div 
            onClick={() => onSelectWorkflow('new')}
            className="group cursor-pointer bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-6 border-2 border-primary-200 hover:border-primary-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="text-center">
              <div className="relative mx-auto w-16 h-16 mb-4">
                <div className="absolute inset-0 bg-primary-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                <svg className="relative w-16 h-16 text-primary-600 mx-auto" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">New Project</h3>
              <p className="text-neutral-600 mb-4">Upload a PDF form and create a new Spring Boot application</p>
              <div className="inline-flex items-center text-primary-700 font-medium group-hover:text-primary-800">
                Upload PDF File
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Continue Session Option */}
          <div 
            onClick={() => {
              onSelectWorkflow('continue');
              onLoadSessions();
            }}
            className="group cursor-pointer bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="text-center">
              <div className="relative mx-auto w-16 h-16 mb-4">
                <div className="absolute inset-0 bg-emerald-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all"></div>
                <svg className="relative w-16 h-16 text-emerald-600 mx-auto" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">Continue Session</h3>
              <p className="text-neutral-600 mb-4">Resume working on a previously saved form project</p>
              <div className="inline-flex items-center text-emerald-700 font-medium group-hover:text-emerald-800">
                Browse Sessions
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowChoice;